import { useEffect, useState } from 'react';
import { downloadDatasetExport, fetchGazeResults } from '../api';
import type { GazeTestResult } from '../types';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  List,
  ListItem,
  ListItemButton,
  Skeleton,
  Stack,
  Chip,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import PageHeader from '../components/PageHeader';
import { formatScreeningLabel } from '../utils/screeningLabel';
import { productTokens } from '../theme';

const DiscoveryIcon = () => (
  <Box sx={{ color: 'primary.main', mb: 1 }}>
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 10H13M10 7V13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

export default function Dashboard() {
  const [results, setResults] = useState<GazeTestResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'json' | 'csv' | null>(null);
  const navigate = useNavigate();
  const { isAuthed } = useAuthStore();

  const handleExport = async (format: 'json' | 'csv') => {
    setExporting(format);
    setError(null);
    try {
      await downloadDatasetExport(format);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '导出失败');
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    if (!isAuthed) return;
    let cancel = false;
    (async () => {
      try {
        const data = await fetchGazeResults();
        if (!cancel) {
          setResults(
            [...data].sort(
              (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
            ),
          );
        }
      } catch (e: unknown) {
        if (!cancel) setError(e instanceof Error ? e.message : '加载失败');
      }
    })();
    return () => {
      cancel = true;
    };
  }, [isAuthed]);

  return (
    <Container
      maxWidth="lg"
      sx={{
        height: '100%',
        minHeight: 0,
        py: { xs: 2, sm: 2.5 },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PageHeader
        title="结果概览"
        subtitle="查看测试记录、快速预览结果，并启动新的眼动流程"
        action={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => void handleExport('json')}
              disabled={exporting !== null}
              sx={{ px: 3.5, py: 1.5, fontWeight: 700 }}
            >
              {exporting === 'json' ? '导出中…' : '导出 JSON 清单'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => void handleExport('csv')}
              disabled={exporting !== null}
              sx={{ px: 3.5, py: 1.5, fontWeight: 700 }}
            >
              {exporting === 'csv' ? '导出中…' : '导出 CSV 清单'}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/test')}
              sx={{ px: 4, py: 1.5, fontWeight: 700 }}
            >
              开始新测试
            </Button>
          </Stack>
        }
      />

      <Card variant="outlined" sx={{ mb: 2, overflow: 'hidden', flex: '1 1 auto', minHeight: 0 }}>
        <CardContent
          sx={{
            p: 0,
            minHeight: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:last-child': { pb: 0 },
          }}
        >
          <Box
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha(productTokens.argus, 0.035),
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6" fontWeight={750} color="primary.dark">
                  历史测试记录
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  所有已完成的上传、结果摘要与可查看的报告
                </Typography>
              </Box>
              {results && results.length > 0 ? (
                <Chip
                  label={`总计 ${results.length} 条记录`}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 700, bgcolor: 'background.paper' }}
                />
              ) : null}
            </Stack>
          </Box>

          {error ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error" variant="outlined">
                {error}
              </Alert>
            </Box>
          ) : null}

          <Box sx={{ p: { xs: 2, sm: 2.5 }, flex: 1, minHeight: 0, overflow: 'auto' }}>
            {results === null ? (
              <Stack spacing={2}>
                <Skeleton variant="rounded" height={80} />
                <Skeleton variant="rounded" height={80} />
                <Skeleton variant="rounded" height={80} />
              </Stack>
            ) : results.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  borderStyle: 'dashed',
                  borderRadius: 1,
                  p: { xs: 3, sm: 5 },
                  textAlign: 'center',
                  bgcolor: alpha(productTokens.argus, 0.025),
                }}
              >
                <Stack alignItems="center" spacing={1}>
                  <DiscoveryIcon />
                  <Typography variant="h6" fontWeight={800}>
                    暂无测试记录
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 400, mb: 2.5 }}
                  >
                    系统尚未检测到任何已完成的测试报告。请启动一次新的测试流程。
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button variant="contained" onClick={() => navigate('/test')} sx={{ px: 4 }}>
                      立即启动测试
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            ) : (
              <List disablePadding>
                {results.map((r, idx) => {
                  const hasScore =
                    typeof r.confidence === 'number' &&
                    Number.isFinite(r.confidence) &&
                    (r.label || '').trim().length > 0;
                  const confidencePct =
                    typeof r.confidence === 'number'
                      ? Math.round(Math.max(0, Math.min(1, r.confidence)) * 100)
                      : null;
                  const qcFailed = r.qc?.passed === false;
                  const display = hasScore
                    ? formatScreeningLabel(r.label)
                    : qcFailed
                      ? { text: 'QC未通过', color: 'warning' as const }
                      : { text: '采集完成', color: 'default' as const };

                  return (
                    <Box key={r.id}>
                      {idx > 0 && <Divider sx={{ my: 1, opacity: 0.6 }} />}
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => navigate(`/results/${r.id}`)}
                          sx={{
                            borderRadius: 1,
                            py: 2,
                            px: 2,
                            border: '1px solid transparent',
                            transition: 'background-color 160ms ease, border-color 160ms ease',
                            '&:hover': {
                              bgcolor: alpha(productTokens.argus, 0.045),
                              borderColor: alpha(productTokens.argus, 0.12),
                            },
                          }}
                        >
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            sx={{ width: '100%' }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 800, color: 'text.primary' }}
                              >
                                {r.name
                                  ? r.name
                                  : r.participantClinicId
                                    ? `受试者 ${r.participantClinicId}`
                                    : '未命名记录'}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.25 }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                  }}
                                >
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 6v6l4 2" />
                                  </svg>
                                  {new Date(r.timestamp).toLocaleString('zh-CN', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </Typography>
                                <Typography variant="caption" color="divider">
                                  |
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  样本 ID: {String(r.id).padStart(8, '0')}
                                </Typography>
                                {r.participantClinicId ? (
                                  <>
                                    <Typography variant="caption" color="divider">
                                      |
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      受试者编号: {r.participantClinicId}
                                    </Typography>
                                  </>
                                ) : null}
                              </Stack>
                            </Box>

                            <Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="center"
                              sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
                            >
                              {r.hasVideo ? (
                                <Chip
                                  label="完整视频"
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 700 }}
                                />
                              ) : null}
                              <Chip
                                label={display.text}
                                color={display.color}
                                size="small"
                                sx={{ fontWeight: 700, borderRadius: 1, px: 1 }}
                              />
                              {hasScore && confidencePct !== null ? (
                                <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="text.secondary"
                                    fontWeight={600}
                                  >
                                    置信度
                                  </Typography>
                                  <Typography variant="body2" fontWeight={800} color="primary.main">
                                    {confidencePct}%
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  {qcFailed ? '未生成结果' : '已保存记录'}
                                </Typography>
                              )}
                              <Box sx={{ color: 'divider', ml: 1 }}>
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M9 18l6-6-6-6" />
                                </svg>
                              </Box>
                            </Stack>
                          </Stack>
                        </ListItemButton>
                      </ListItem>
                    </Box>
                  );
                })}
              </List>
            )}
          </Box>
        </CardContent>
      </Card>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          bgcolor: alpha(productTokens.argus, 0.025),
          borderStyle: 'dashed',
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          当前控制台默认以采集为主：保存完整视频、参考状态标签和元数据，用于后续离线分析与训练整理。
        </Typography>
      </Paper>
    </Container>
  );
}
